/**
 * Connector wizard types — DRY, schema-driven contract for every
 * connector capability in v2 (port of v1 `EnterpriseConnectorConfig<TConn>`,
 * generalised from "enterprise" to "capability" so OAuth-only vendors,
 * file-library vendors and PSD2 banking flows all share one shell).
 *
 * Per CLAUDE.md "white-label naming OBBLIGATORIO": vendor names (`acube`,
 * `enable_banking`, `fabrick`, …) live ONLY inside per-vendor adapters.
 * Routes / URLs / DB / FE-facing strings address the *capability* slug
 * (`cassetto_fiscale`, `open_banking`, `sdi_invoicing`, …). PEC is the only
 * exception — `aruba_pec` / `infocert_pec` are spec'd explicitly.
 *
 * v1 source-of-truth references:
 *   ando-frontend/app/components/enterprise/connector-page/types.ts
 *   ando-frontend/app/pages/configs/salesforceConfig.tsx
 *   ando-frontend/app/pages/configs/fattureInCloudConfig.tsx
 */

import type { I18n, MessageDescriptor } from "@lingui/core";
import type * as React from "react";

// ---------------------------------------------------------------------------
// Localized string carrier — spec authors pass the macro output
// (``msg`Testo`` returns a ``MessageDescriptor``) so the actual string is
// resolved at render-time against the active Lingui catalog. Plain ``string``
// values stay supported as the back-compat / "not translatable" path
// (vendor ids, brand names, etc.). All consumers MUST go through
// ``localize`` to get a renderable string.
// ---------------------------------------------------------------------------

export type LocalizedText = string | MessageDescriptor;

/**
 * Resolve a ``LocalizedText`` against the active Lingui catalog.
 * Plain strings pass through unchanged; ``MessageDescriptor`` values are
 * looked up via ``i18n._()``. Consumers obtain ``i18n`` from
 * ``useLingui()`` so the resolved string updates whenever the active
 * locale switches.
 */
export function localize(value: LocalizedText, i18n: I18n): string {
  return typeof value === "string" ? value : i18n._(value);
}

/** Like ``localize`` but tolerant of ``undefined``/``null`` — common for
 *  optional fields (``description``, ``buttonLabel``, …). Returns
 *  ``undefined`` when no value, otherwise the resolved string. */
export function localizeOpt(
  value: LocalizedText | undefined | null,
  i18n: I18n,
): string | undefined {
  if (value == null) return undefined;
  return localize(value, i18n);
}

// ---------------------------------------------------------------------------
// Capability slugs — white-label, vendor-agnostic. Add new slugs in lockstep
// with `src/capabilities/` on the BE; never embed a vendor name here.
// ---------------------------------------------------------------------------

export type CapabilitySlug =
  | "crm"
  | "project_management"
  | "accounting"
  | "calendar"
  | "messaging"
  | "drive"
  | "email"
  | "open_banking"
  | "filesystem"
  // PEC vendor-specific exception per memory rule
  // `feedback_white_label_capability_naming.md`: PEC drivers carry the
  // vendor in the capability slug (aruba_pec / infocert_pec) because
  // the underlying legal framework is vendor-specific and the user
  // chooses one explicitly.
  | "pec"
  | "aruba_pec"
  | "infocert_pec"
  // NOTE: Aruba Mail is NOT a capability — it's a VENDOR under ``email``
  // (email + app-password, no OAuth). See ``emailSpec.vendors``.
  | "sdi_invoicing"
  | "cassetto_fiscale"
  | "vat_verifier"
  | "legal_storage"
  | "peppol_gateway"
  | "invoice_extractor"
  | "cad_library"
  // Spreadsheet imports (Excel / CSV / Google Sheets). PR-INT-2 — V1
  // "Foglio di Calcolo" parity. Multi-source: local upload, Google
  // Drive cloud-file, OneDrive cloud-file. Not OAuth — the wizard mounts
  // a custom dialog that hits ``POST /enterprise/connectors/import-spreadsheet``
  // (multipart) and routes the user to the dashboard post-connect.
  | "spreadsheet"
  | "database"
  | "erp";

// ---------------------------------------------------------------------------
// Base shape every connection row must satisfy. Per-capability `TConn`
// extends this with vendor-specific fields (api_version, scope, vat, …).
// ---------------------------------------------------------------------------

export interface BaseConnection {
  /** Stable identifier — the wizard reads this through `connectionKeyField`. */
  readonly id?: string;
  /** Vendor producing this connection (slack vs teams vs google_chat, …). */
  readonly vendor?: string;
  /**
   * BE-side `user_connections.connection_key` — the unique-per-vendor
   * identifier (mailbox address for email, workspace_id for chat, org_id
   * for CRM, …). Specs read this in `getDisplayName` so the FE always has
   * a human-friendly label without an extra round-trip.
   */
  readonly connection_key?: string;
  /** Lifecycle marker — `"needs_reauth"` is the only "broken" signal. */
  readonly token_status?: "active" | "needs_reauth";
  /** True when the long-lived refresh token is dead. */
  readonly needs_reauth?: boolean;
  /** Optional ISO timestamp of the last successful API call. */
  readonly last_used_at?: string | null;
  /** Optional ISO timestamp the user connected the vendor. */
  readonly connected_at?: string | null;
  /** Vendor-specific freeform metadata projected from `user_connections.metadata`. */
  readonly metadata?: Record<string, unknown>;
}

// ---------------------------------------------------------------------------
// API adapter — every capability binds its REST surface to this shape so
// `<ConnectorWizard>` stays vendor-agnostic. Mirrors v1 `ConnectorApi<TConn>`
// extended with `listConnections` (alias for `getConnections` kept for the
// v1 port) and `test` (optional dry-run health check).
// ---------------------------------------------------------------------------

export interface ConnectorStatus {
  readonly enabled: boolean;
  readonly managed_app_available?: boolean;
  readonly callback_url?: string;
  readonly [key: string]: unknown;
}

export interface ConnectOpts {
  readonly sandbox?: boolean;
  readonly client_id?: string;
  readonly client_secret?: string;
  readonly tenant_code?: string;
  readonly [key: string]: unknown;
}

export interface ConnectResult {
  /** OAuth flows return a redirect URL the host page navigates to. */
  readonly redirect_url?: string;
  /** Synchronous flows (API key, manual creds) return the persisted row id. */
  readonly connection_id?: string;
}

export interface ConnectorApi<TConn extends BaseConnection> {
  getStatus(): Promise<ConnectorStatus>;
  listConnections(): Promise<readonly TConn[]>;
  connect(userId: string, opts: ConnectOpts): Promise<ConnectResult>;
  disconnect(connectionKey: string): Promise<void>;
  sync(connectionKey: string): Promise<{ readonly in_flight: boolean }>;
  /** Optional health probe — wizards surface it as "Verifica". */
  test?(connectionKey: string): Promise<{ readonly ok: boolean; readonly error?: string }>;
}

// ---------------------------------------------------------------------------
// Wizard step DSL — `kind` discriminates the renderer, every step carries an
// `id` so the indicator can build a stable ladder. Vendor specifics ride in
// via the optional `body` slot so capability specs can stay declarative.
// ---------------------------------------------------------------------------

export type WizardStepKind =
  | "vendor_picker" // multi-vendor capability → pick provider first
  | "oauth" // redirect to managed / BYOA OAuth
  | "credentials" // API key, basic auth, NAS host/port
  | "select" // pick scope (calendars, workspace, channels, …)
  | "form" // arbitrary schema-driven form
  | "confirm"; // review + submit

export interface FieldSpec {
  readonly name: string;
  readonly label: LocalizedText;
  readonly type:
    | "string"
    | "password"
    | "number"
    | "boolean"
    | "email"
    | "url"
    | "select"
    | "tenant_code"
    | "callback_readonly";
  readonly required?: boolean;
  readonly placeholder?: LocalizedText;
  readonly helperText?: LocalizedText;
  readonly options?: ReadonlyArray<{ readonly value: string; readonly label: LocalizedText }>;
}

export interface VendorOption {
  readonly id: string;
  readonly label: LocalizedText;
  readonly icon?: React.ReactNode;
  readonly description?: LocalizedText;
  /** White-label marker — `true` for vendors hidden behind a capability. */
  readonly whiteLabel?: boolean;
  /**
   * Partner-gated / not-yet-shipped vendors — surfaced in the picker with an
   * "In arrivo" pill and ``aria-disabled``. Click is suppressed so the
   * caller never receives a ``onPickVendor`` event for a disabled entry.
   *
   * Use this for ICP tier-4 drivers (Zucchetti, TeamSystem, …) that we want
   * to advertise in the UI before the BE driver lands — keeps the catalog
   * honest and gives Sales a story without surfacing red errors at click.
   */
  readonly disabled?: boolean;
  /**
   * Optional short label (e.g. ``"In arrivo"``, ``"Beta"``) painted next to
   * the vendor name when ``disabled === true`` so users understand WHY
   * the row is greyed out. Defaults to ``DEFAULT_DISABLED_REASON``.
   */
  readonly disabledReason?: LocalizedText;
}

/**
 * Fallback label rendered next to a ``disabled`` vendor when the spec
 * doesn't set ``VendorOption.disabledReason``. Single source of truth so
 * the UI copy stays consistent across the picker dropdown, the empty-state
 * grid and the single-vendor CTA button.
 */
export const DEFAULT_DISABLED_REASON = "In arrivo";

export interface WizardStepSpec<TConn extends BaseConnection = BaseConnection> {
  readonly id: string;
  readonly kind: WizardStepKind;
  readonly title: LocalizedText;
  readonly description?: LocalizedText;
  /** Fields rendered when `kind === "credentials" | "form"`. */
  readonly fields?: readonly FieldSpec[];
  /**
   * Vendor list rendered when `kind === "vendor_picker"`. Multi-vendor
   * capabilities (messaging, drive, calendar, …) must supply this; the
   * picked vendor flows down to subsequent steps via `state.vendor`.
   */
  readonly vendors?: readonly VendorOption[];
  /**
   * Optional render slot — when the declarative DSL can't express a step
   * (e.g. PSD2 country / bank picker), spec returns custom JSX.
   *
   * Receives the cumulative form `state` so multi-step flows can read prior
   * input, plus an imperative `next(patch)` to advance the wizard.
   */
  readonly body?: (ctx: StepRenderCtx<TConn>) => React.ReactNode;
  /**
   * For `oauth` steps: produce the redirect URL. Receives partial form
   * state (sandbox toggle, tenant_code, …) so multi-tenant flows can pass
   * them up to `api.connect()`.
   */
  readonly oauth?: {
    readonly buttonLabel?: LocalizedText;
    /**
     * Optional secondary CTA (e.g. v1 SF "BYOA" tab). Wizard treats it as a
     * second `connect()` call with `client_id`/`client_secret` from `state`.
     */
    readonly secondaryLabel?: LocalizedText;
  };
  /**
   * Hide this step when the predicate returns `true` against current state.
   * Lets specs skip `vendor_picker` for single-vendor capabilities.
   */
  readonly skipIf?: (state: WizardState) => boolean;
  /**
   * Scope this step to a single vendor inside a multi-vendor capability.
   * When set, ``ConnectAccountDialog`` only selects this credentials step
   * if the user picked the matching vendor via ``AddAccountMenu``. Lets a
   * capability ship per-vendor field sets (e.g. ``database``: SQLite needs
   * a file_path, ODBC needs dsn/connection_string, others need host/port)
   * without per-vendor capability slugs. Steps without ``vendor`` act as
   * the default (used when no vendor-scoped step matches).
   */
  readonly vendor?: string;
}

export type WizardState = Record<string, unknown>;

export interface StepRenderCtx<TConn extends BaseConnection> {
  readonly spec: CapabilityWizardSpec<TConn>;
  readonly state: WizardState;
  /** Patch the form state — wizard re-renders. */
  readonly patch: (patch: Partial<WizardState>) => void;
  /** Submit current state to `api.connect()` and advance. */
  readonly submit: () => Promise<void>;
  /** Move to the next step without submitting. */
  readonly next: () => void;
  /** Move back one step. */
  readonly prev: () => void;
  /** True while a `connect()` / `sync()` is in flight. */
  readonly submitting: boolean;
  /** Latest connect result (after `submit()`) — useful for confirm screens. */
  readonly result: ConnectResult | null;
}

// ---------------------------------------------------------------------------
// Compliance / benefits — rendered in the empty state (same shape as v1).
// ---------------------------------------------------------------------------

export interface BenefitBullet {
  readonly icon: React.ReactNode;
  readonly title: LocalizedText;
  readonly body: LocalizedText;
}

// ---------------------------------------------------------------------------
// The main spec object. One per capability — `crmSpec`, `accountingSpec`, …
// ---------------------------------------------------------------------------

export interface CapabilityWizardSpec<TConn extends BaseConnection = BaseConnection> {
  // ── Identity ────────────────────────────────────────────────────────────
  readonly capability: CapabilitySlug;
  readonly title: LocalizedText;
  readonly subtitle: LocalizedText;
  /**
   * White-label noun for ONE connected unit of this capability. Drives the
   * "+ Aggiungi {noun}" CTA, the status pill, the section heading and the
   * empty state. Defaults to "account" so every existing spec is unchanged.
   * Italian plurals/genders aren't regular, so pass all three.
   */
  readonly connectNoun?: string; // singular, lower-case — "cartella"
  readonly connectNounPlural?: string; // plural,   lower-case — "cartelle"
  readonly connectNounGender?: "m" | "f"; // grammatical agreement (default "m")
  readonly icon: React.ReactNode;
  /**
   * GDPR-mandated post-disconnect deletion window shown in the disconnect
   * dialog. v1 default = 30 min.
   */
  readonly gdprDeleteWindowMinutes: number;

  // ── Vendors (single-vendor capabilities keep this to one entry) ────────
  readonly vendors: readonly VendorOption[];

  // ── Wizard flow ────────────────────────────────────────────────────────
  readonly steps: readonly WizardStepSpec<TConn>[];

  // ── Benefits grid (empty state) ────────────────────────────────────────
  readonly benefits: readonly BenefitBullet[];

  // ── Data layer (injected; wizard does no fetching of its own) ──────────
  readonly api: ConnectorApi<TConn>;

  // ── Connection display helpers ─────────────────────────────────────────
  readonly connectionKeyField: keyof TConn;
  readonly getDisplayName: (conn: TConn) => string;
  readonly getSecondaryId?: (conn: TConn) => string | null;
  /** Optional badges shown next to the connection title (sandbox/prod, …). */
  readonly renderConnectionMeta?: (conn: TConn) => React.ReactNode;
  /**
   * Optional brand icon per row — used by `<ConnectionList>` to render a
   * vendor-coloured tile next to the display name (Gmail red, Outlook blue
   * …). Falls back to `spec.icon` when unset, so single-vendor specs work
   * without any extra wiring.
   */
  readonly getDisplayIcon?: (conn: TConn) => React.ReactNode;
  /**
   * Optional brand colour (hex) per row — drives the left strip on the
   * row card so vendors are visually distinct in a multi-vendor capability.
   */
  readonly getBrandColor?: (conn: TConn) => string | null;
}

// ---------------------------------------------------------------------------
// Helper: read the connection key off a typed row.
// ---------------------------------------------------------------------------

export function getConnectionKey<TConn extends BaseConnection>(
  conn: TConn,
  field: keyof TConn,
): string {
  const raw = conn[field];
  return raw == null ? "" : String(raw);
}

// ---------------------------------------------------------------------------
// Helper: white-label "account" noun → all the derived UI strings (DRY).
// One ``connectNoun`` on the spec drives the CTA / pill / heading / empty
// state, with correct Italian gender+number agreement.
// ---------------------------------------------------------------------------

export interface ConnectNoun {
  readonly singular: string;
  readonly plural: string;
  /** "Aggiungi cartella" — header CTA + empty-state CTA. */
  readonly addLabel: string;
  /** "Cartelle collegate" — section heading. */
  readonly sectionLabel: string;
  /** "Nessuna cartella collegata" — empty-state pill. */
  readonly emptyLabel: string;
  /** n → "1 cartella collegata" / "3 cartelle collegate". */
  readonly countLabel: (n: number) => string;
}

export function resolveConnectNoun(
  spec: Pick<CapabilityWizardSpec, "connectNoun" | "connectNounPlural" | "connectNounGender">,
): ConnectNoun {
  const singular = spec.connectNoun ?? "account";
  const plural = spec.connectNounPlural ?? singular; // "account" is invariant
  const fem = spec.connectNounGender === "f";
  const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
  const agreeS = fem ? "collegata" : "collegato";
  const agreeP = fem ? "collegate" : "collegati";
  return {
    singular,
    plural,
    addLabel: `Aggiungi ${singular}`,
    sectionLabel: `${cap(plural)} ${agreeP}`,
    emptyLabel: `${fem ? "Nessuna" : "Nessun"} ${singular} ${agreeS}`,
    countLabel: (n) => (n === 1 ? `${n} ${singular} ${agreeS}` : `${n} ${plural} ${agreeP}`),
  };
}
